import { Box, Button, Group, Paper, Stack, Text } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { CalendarEvent } from 'src/meetings/models';
import { useRegenerateTranscriptSummary } from 'src/meetings/hooks/useRegenerateTranscriptSummary';
import { getAllClientParticipants } from 'src/meetings/utils/event-utils';
import { v4 as uuidv4 } from 'uuid';
import { NavLink } from 'react-router';
import { useFileUpload } from 'src/data-capture/hooks/file-uploads/useFileUpload';
import { useFlags } from 'src/feature-flags/FlagsHook';
import RecordingProcessingFailureMessage from 'src/meetings/detail/tabs/RecordingProcessingFailureMessage';
import { RecordingDataActions } from './RecordingDataActions';
import { Transcript } from './transcript/types';

type Props = {
  event: CalendarEvent;
  transcript: Transcript;
  hideSuggestedActions?: boolean;
};

export const SummaryTab = ({ event, transcript, hideSuggestedActions = false }: Props) => {
  const clientParticipants = getAllClientParticipants(event);
  const recordingOwnerClientId = transcript?.recordingOwnerClientId ?? undefined;
  const { file: recordingDataUploadFile } = useFileUpload(transcript?.dataUploadFileId, {
    clientId: recordingOwnerClientId,
    enabled: clientParticipants.length === 1 && !!recordingOwnerClientId,
  });
  const { flagEnabledMeetingsRework } = useFlags();
  const { regenerateSummary, isRegenerating } = useRegenerateTranscriptSummary(event.id);

  const showMultiClientDataActions =
    !hideSuggestedActions &&
    flagEnabledMeetingsRework &&
    clientParticipants.length > 1 &&
    !!transcript?.dataUploadFileId;

  const isGenerating = isRegenerating || transcript?.summaryStatus === 'IN_PROGRESS';
  const canGenerate = event.hasTranscription && !transcript?.summary;

  const getSummaryContent = () => {
    if (transcript?.summary) {
      return (
        <Stack gap="xs">
          <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10">
            Overview
          </Text>
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
            {transcript?.summary}
          </Text>
        </Stack>
      );
    }

    return (
      <Box>
        <Text size="sm" c="gray.7">
          {getSummaryMissingContent()}
        </Text>
      </Box>
    );
  };

  const getSummaryMissingContent = () => {
    if (!event.hasTranscription) {
      return 'A transcription is required to generate a summary.';
    }

    if (transcript?.status === 'FAILED') {
      return <RecordingProcessingFailureMessage />;
    }

    if (isGenerating) {
      return 'Generating a summary. This can take a few moments.';
    }

    // The recording processed, so this is a failure to summarise it rather than the recording
    // failure that message describes.
    if (transcript?.summaryStatus === 'ERROR') {
      return 'The summary could not be generated.';
    }

    return 'No summary available';
  };

  return (
    <>
      {getSummaryContent()}

      {canGenerate && (
        <Group mt="sm">
          <Button
            variant="subtle"
            leftSection={<IconRefresh size={16} />}
            loading={isGenerating}
            onClick={regenerateSummary}
          >
            Generate summary
          </Button>
        </Group>
      )}

      {!hideSuggestedActions &&
        recordingDataUploadFile &&
        !recordingDataUploadFile.isResolved &&
        clientParticipants.length === 1 && (
          <Stack gap="xs" mt={'lg'}>
            <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10">
              Suggested actions
            </Text>
            <Paper variant="card" w={'328px'}>
              <Text size="sm" fw={FONT_WEIGHTS.semibold} c="gray.10">
                Add data to client record
              </Text>
              <Text mt="xxxs" mb="xs" size="xs" fw={FONT_WEIGHTS.medium} c="gray.10">
                Extract client data from this meeting note to add/update their record.
              </Text>
              <NavLink
                to={`/client/${recordingOwnerClientId}/detected-entities?fileId=${transcript?.dataUploadFileId}&fileName=${encodeURIComponent(event.title)}&guid=${uuidv4()}&originMeetingId=${event.id}`}
                style={{ textDecoration: 'none' }}
              >
                <Text size="xs" fw={FONT_WEIGHTS.medium} c="brand.6">
                  Review data
                </Text>
              </NavLink>
            </Paper>
          </Stack>
        )}

      {showMultiClientDataActions && <RecordingDataActions event={event} />}
    </>
  );
};
